import React, { useId, useRef } from 'react';
import './FormCheckbox.css';

/**
 * FormCheckbox component with ArbiWin design system integration
 * Supports single checkboxes, checkbox groups, and radio button groups
 *
 * @param {Object} props - FormCheckbox properties
 * @param {boolean} props.checked - Checked state
 * @param {boolean} props.defaultChecked - Default checked state
 * @param {Function} props.onChange - Change handler
 * @param {Function} props.onBlur - Blur handler
 * @param {Function} props.onFocus - Focus handler
 * @param {string} props.name - Field name
 * @param {string} props.value - Checkbox value
 * @param {string} props.label - Checkbox label
 * @param {React.ReactNode} props.children - Alternative to label prop
 * @param {boolean} props.required - Mark as required
 * @param {boolean} props.disabled - Disable checkbox
 * @param {boolean} props.indeterminate - Indeterminate state
 * @param {string} props.error - Error message
 * @param {string} props.helperText - Helper text
 * @param {string} props.size - Checkbox size: 'small', 'medium', 'large'
 * @param {string} props.variant - Checkbox variant: 'default', 'card'
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @returns {JSX.Element} FormCheckbox component
 */
const FormCheckbox = function FormCheckbox({
  checked,
  defaultChecked,
  onChange,
  onBlur,
  onFocus,
  name,
  value,
  label,
  children,
  required = false,
  disabled = false,
  indeterminate = false,
  error,
  helperText,
  size = 'medium',
  variant = 'default',
  className = '',
  testId,
  ...rest
}) {

  const checkboxId = useId();
  const errorId = useId();
  const helperTextId = useId();
  const inputRef = useRef(null);

  const hasError = Boolean(error);
  const shouldShowHelperText = Boolean(helperText && !hasError);
  const shouldShowError = Boolean(error);
  const labelContent = label || children;

  const checkboxClasses = [
    'arbiwin-form-checkbox',
    'arbiwin-form-checkbox--' + size,
    'arbiwin-form-checkbox--' + variant,
    hasError ? 'arbiwin-form-checkbox--error' : '',
    disabled ? 'arbiwin-form-checkbox--disabled' : '',
    indeterminate ? 'arbiwin-form-checkbox--indeterminate' : '',
    className
  ].filter(Boolean).join(' ');

  const handleChange = function(event) {
    if (onChange) {
      onChange(event);
    }
  };

  const handleWrapperClick = function(event) {
    // If clicking on the visual box, wrapper, or SVG elements (but not the label text), toggle the checkbox
    // The label's htmlFor will handle clicks on the label text
    const isBox = event.target.classList.contains('arbiwin-form-checkbox__box');
    const isCheckmark = event.target.classList.contains('arbiwin-form-checkbox__checkmark') || event.target.closest('.arbiwin-form-checkbox__checkmark');
    const isIndeterminate = event.target.classList.contains('arbiwin-form-checkbox__indeterminate') || event.target.closest('.arbiwin-form-checkbox__indeterminate');
    const isWrapper = event.target === event.currentTarget;
    
    if (!disabled && (isBox || isCheckmark || isIndeterminate || isWrapper)) {
      event.preventDefault();
      // Programmatically click the input to trigger the change event
      if (inputRef.current) {
        inputRef.current.click();
      }
    }
  };

  const describedBy = [];
  if (shouldShowError) describedBy.push(errorId);
  if (shouldShowHelperText) describedBy.push(helperTextId);

  return (
    <div className={checkboxClasses}>
      <div className="arbiwin-form-checkbox__wrapper" onClick={handleWrapperClick}>
        <input
          id={checkboxId}
          type="checkbox"
          name={name}
          value={value}
          checked={checked}
          defaultChecked={defaultChecked}
          onChange={handleChange}
          onBlur={onBlur}
          onFocus={onFocus}
          required={required}
          disabled={disabled}
          className="arbiwin-form-checkbox__input"
          aria-invalid={hasError}
          aria-describedby={describedBy.length > 0 ? describedBy.join(' ') : undefined}
          data-testid={testId || 'arbiwin-form-checkbox'}
          ref={function(input) {
            inputRef.current = input;
            if (input) {
              input.indeterminate = indeterminate;
            }
          }}
          {...rest}
        />

        <div className="arbiwin-form-checkbox__box">
          <svg
            className="arbiwin-form-checkbox__checkmark"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
          <svg
            className="arbiwin-form-checkbox__indeterminate"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z"
              clipRule="evenodd"
            />
          </svg>
        </div>

        {labelContent && (
          <label
            htmlFor={checkboxId}
            className="arbiwin-form-checkbox__label"
          >
            {labelContent}
            {required && (
              <span className="arbiwin-form-checkbox__required" aria-label="задължително поле">
                *
              </span>
            )}
          </label>
        )}
      </div>

      {shouldShowError && (
        <div
          id={errorId}
          className="arbiwin-form-checkbox__error"
          role="alert"
          aria-live="polite"
        >
          <svg
            className="arbiwin-form-checkbox__error-icon"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          {error}
        </div>
      )}

      {shouldShowHelperText && (
        <div
          id={helperTextId}
          className="arbiwin-form-checkbox__helper-text"
        >
          {helperText}
        </div>
      )}
    </div>
  );
};

/**
 * FormRadio component for radio button inputs
 *
 * @param {Object} props - FormRadio properties
 * @returns {JSX.Element} FormRadio component
 */
const FormRadio = function FormRadio({
  checked,
  defaultChecked,
  onChange,
  onBlur,
  onFocus,
  name,
  value,
  label,
  children,
  required = false,
  disabled = false,
  error,
  helperText,
  size = 'medium',
  variant = 'default',
  className = '',
  testId,
  ...rest
}) {

  const radioId = useId();
  const errorId = useId();
  const helperTextId = useId();

  const hasError = Boolean(error);
  const shouldShowHelperText = Boolean(helperText && !hasError);
  const shouldShowError = Boolean(error);
  const labelContent = label || children;

  const radioClasses = [
    'arbiwin-form-radio',
    'arbiwin-form-radio--' + size,
    'arbiwin-form-radio--' + variant,
    hasError ? 'arbiwin-form-radio--error' : '',
    disabled ? 'arbiwin-form-radio--disabled' : '',
    className
  ].filter(Boolean).join(' ');

  const handleChange = function(event) {
    if (onChange) {
      onChange(event);
    }
  };

  const describedBy = [];
  if (shouldShowError) describedBy.push(errorId);
  if (shouldShowHelperText) describedBy.push(helperTextId);

  return (
    <div className={radioClasses}>
      <div className="arbiwin-form-radio__wrapper">
        <input
          id={radioId}
          type="radio"
          name={name}
          value={value}
          checked={checked}
          defaultChecked={defaultChecked}
          onChange={handleChange}
          onBlur={onBlur}
          onFocus={onFocus}
          required={required}
          disabled={disabled}
          className="arbiwin-form-radio__input"
          aria-describedby={describedBy.length > 0 ? describedBy.join(' ') : undefined}
          data-testid={testId || 'arbiwin-form-radio'}
          {...rest}
        />

        <div className="arbiwin-form-radio__circle">
          <div className="arbiwin-form-radio__dot" />
        </div>

        {labelContent && (
          <label
            htmlFor={radioId}
            className="arbiwin-form-radio__label"
          >
            {labelContent}
            {required && (
              <span className="arbiwin-form-radio__required" aria-label="задължително поле">
                *
              </span>
            )}
          </label>
        )}
      </div>

      {shouldShowError && (
        <div
          id={errorId}
          className="arbiwin-form-radio__error"
          role="alert"
          aria-live="polite"
        >
          <svg
            className="arbiwin-form-radio__error-icon"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          {error}
        </div>
      )}

      {shouldShowHelperText && (
        <div
          id={helperTextId}
          className="arbiwin-form-radio__helper-text"
        >
          {helperText}
        </div>
      )}
    </div>
  );
};

/**
 * FormCheckboxGroup component for grouping multiple checkboxes
 *
 * @param {Object} props - FormCheckboxGroup properties
 * @param {Array} props.options - Array of option objects {value, label, disabled}
 * @param {Array} props.value - Array of selected values
 * @param {Function} props.onChange - Change handler
 * @param {string} props.name - Group name
 * @param {string} props.label - Group label
 * @param {string} props.error - Error message
 * @param {string} props.helperText - Helper text
 * @param {boolean} props.required - Mark group as required
 * @param {boolean} props.disabled - Disable all checkboxes
 * @param {string} props.direction - Layout direction: 'vertical', 'horizontal'
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} FormCheckboxGroup component
 */
const FormCheckboxGroup = function FormCheckboxGroup({
  options = [],
  value = [],
  onChange,
  name,
  label,
  error,
  helperText,
  required = false,
  disabled = false,
  direction = 'vertical',
  className = '',
  testId
}) {

  const groupId = useId();
  const errorId = useId();
  const helperTextId = useId();

  const hasError = Boolean(error);
  const shouldShowHelperText = Boolean(helperText && !hasError);
  const shouldShowError = Boolean(error);

  const groupClasses = [
    'arbiwin-form-checkbox-group',
    'arbiwin-form-checkbox-group--' + direction,
    hasError ? 'arbiwin-form-checkbox-group--error' : '',
    disabled ? 'arbiwin-form-checkbox-group--disabled' : '',
    className
  ].filter(Boolean).join(' ');

  const handleOptionChange = function(optionValue, checked) {
    const newValue = checked
      ? value.concat([optionValue])
      : value.filter(function(v) { return v !== optionValue; });

    if (onChange) {
      const event = {
        target: {
          name: name,
          value: newValue
        }
      };
      onChange(event);
    }
  };

  const describedBy = [];
  if (shouldShowError) describedBy.push(errorId);
  if (shouldShowHelperText) describedBy.push(helperTextId);

  return (
    <fieldset
      className={groupClasses}
      aria-invalid={hasError}
      aria-describedby={describedBy.length > 0 ? describedBy.join(' ') : undefined}
      data-testid={testId || 'arbiwin-form-checkbox-group'}
    >
      {label && (
        <legend className="arbiwin-form-checkbox-group__label">
          {label}
          {required && (
            <span className="arbiwin-form-checkbox-group__required" aria-label="задължително поле">
              *
            </span>
          )}
        </legend>
      )}

      <div className="arbiwin-form-checkbox-group__options">
        {options.map(function(option) {
          const isChecked = value.includes(option.value);
          const isDisabled = disabled || option.disabled;

          return (
            <FormCheckbox
              key={option.value}
              name={name}
              value={option.value}
              checked={isChecked}
              disabled={isDisabled}
              label={option.label}
              onChange={function(event) {
                handleOptionChange(option.value, event.target.checked);
              }}
            />
          );
        })}
      </div>

      {shouldShowError && (
        <div
          id={errorId}
          className="arbiwin-form-checkbox-group__error"
          role="alert"
          aria-live="polite"
        >
          <svg
            className="arbiwin-form-checkbox-group__error-icon"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          {error}
        </div>
      )}

      {shouldShowHelperText && (
        <div
          id={helperTextId}
          className="arbiwin-form-checkbox-group__helper-text"
        >
          {helperText}
        </div>
      )}
    </fieldset>
  );
};

/**
 * FormRadioGroup component for grouping radio buttons
 *
 * @param {Object} props - FormRadioGroup properties
 * @param {Array} props.options - Array of option objects {value, label, disabled}
 * @param {string} props.value - Selected value
 * @param {Function} props.onChange - Change handler
 * @param {string} props.name - Group name
 * @param {string} props.label - Group label
 * @param {string} props.error - Error message
 * @param {string} props.helperText - Helper text
 * @param {boolean} props.required - Mark group as required
 * @param {boolean} props.disabled - Disable all radio buttons
 * @param {string} props.direction - Layout direction: 'vertical', 'horizontal'
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} FormRadioGroup component
 */
const FormRadioGroup = function FormRadioGroup({
  options = [],
  value,
  onChange,
  name,
  label,
  error,
  helperText,
  required = false,
  disabled = false,
  direction = 'vertical',
  className = '',
  testId
}) {

  const groupId = useId();
  const errorId = useId();
  const helperTextId = useId();

  const hasError = Boolean(error);
  const shouldShowHelperText = Boolean(helperText && !hasError);
  const shouldShowError = Boolean(error);

  const groupClasses = [
    'arbiwin-form-radio-group',
    'arbiwin-form-radio-group--' + direction,
    hasError ? 'arbiwin-form-radio-group--error' : '',
    disabled ? 'arbiwin-form-radio-group--disabled' : '',
    className
  ].filter(Boolean).join(' ');

  const handleOptionChange = function(event) {
    if (onChange) {
      onChange(event);
    }
  };

  const describedBy = [];
  if (shouldShowError) describedBy.push(errorId);
  if (shouldShowHelperText) describedBy.push(helperTextId);

  return (
    <fieldset
      className={groupClasses}
      aria-invalid={hasError}
      aria-describedby={describedBy.length > 0 ? describedBy.join(' ') : undefined}
      data-testid={testId || 'arbiwin-form-radio-group'}
    >
      {label && (
        <legend className="arbiwin-form-radio-group__label">
          {label}
          {required && (
            <span className="arbiwin-form-radio-group__required" aria-label="задължително поле">
              *
            </span>
          )}
        </legend>
      )}

      <div className="arbiwin-form-radio-group__options">
        {options.map(function(option) {
          const isChecked = value === option.value;
          const isDisabled = disabled || option.disabled;

          return (
            <FormRadio
              key={option.value}
              name={name}
              value={option.value}
              checked={isChecked}
              disabled={isDisabled}
              required={required}
              label={option.label}
              onChange={handleOptionChange}
            />
          );
        })}
      </div>

      {shouldShowError && (
        <div
          id={errorId}
          className="arbiwin-form-radio-group__error"
          role="alert"
          aria-live="polite"
        >
          <svg
            className="arbiwin-form-radio-group__error-icon"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          {error}
        </div>
      )}

      {shouldShowHelperText && (
        <div
          id={helperTextId}
          className="arbiwin-form-radio-group__helper-text"
        >
          {helperText}
        </div>
      )}
    </fieldset>
  );
};

FormCheckbox.displayName = 'FormCheckbox';
FormRadio.displayName = 'FormRadio';
FormCheckboxGroup.displayName = 'FormCheckboxGroup';
FormRadioGroup.displayName = 'FormRadioGroup';

export default FormCheckbox;
export { FormRadio, FormCheckboxGroup, FormRadioGroup };
